import React, { useState, useEffect, useCallback } from "react";
import { useWarehouseContext } from "./WarehouseProvider";
import { StepHeader } from "./components/StepHeader";
import { FullscreenModal } from "./components/FullscreenModal";
import { useSteps } from "./useSteps";
import { CircularProgress } from "@mui/material";
import { Check, Package, Trash2 } from "lucide-react";
import {
  WarehouseButtonsFooter,
  WarehouseButton,
  WarehouseButtonGroup,
} from "./components/WarehouseButtonsFooter";

const TITLE = "Processing Confirmation";

export const ProcessingConfirmation = () => {
  const [selectedOption, setSelectedOption] = useState("");
  const [description, setDescription] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const { updateTakeback, clearTakeback } = useWarehouseContext();
  const { exit, jumpTo } = useSteps();

  const options = [
    { id: "accepted", label: "Accepted", icon: Check },
    { id: "returned", label: "Returned", icon: Package },
    { id: "disposed", label: "Disposed", icon: Trash2 },
    { id: "other", label: "Other", icon: null },
  ];

  const onBack = () => {
    // Jump back to Summary
    const Summary = require("./Summary").Summary;
    jumpTo(Summary);
  };

  const onFinish = useCallback(async () => {
    // If "returned" is selected, show the return to seller info screen
    if (selectedOption === "returned") {
      useSteps.getState().showReturnToSellerInfo(selectedOption, description);
      return;
    }

    try {
      setIsUpdating(true);
      // Pass resolution data to updateTakeback
      updateTakeback({
        resolutionCategory: selectedOption,
        resolutionReason: description,
      });
      clearTakeback();
      exit({ successMessage: "Successfully processed package" });
    } catch (e) {
      console.error("Failed to process package:", e);
    } finally {
      setIsUpdating(false);
    }
  }, [selectedOption, description, updateTakeback, clearTakeback, exit]);

  // Handle Enter key to trigger Finish button
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle Enter key if not in the input field
      if (e.key === "Enter" && e.target.tagName !== "INPUT" && !isUpdating) {
        e.preventDefault();
        onFinish();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedOption, description, isUpdating, onFinish]);

  return (
    <FullscreenModal>
      <div className="flex flex-col h-full">
        <StepHeader />

        <div className="flex-1 flex flex-col items-center justify-center px-4">
          <div className="w-full max-w-4xl">
            <h1 className="text-white text-[29px] font-bold text-center mb-2 leading-[37px] tracking-[-0.01em]">
              Complete Processing
            </h1>
            <p className="text-[#C6CCE8] text-base font-medium text-center mb-8 leading-[22px]">
              Select a processing outcome to continue
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              {options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setSelectedOption(option.id)}
                  className={`p-6 rounded-2xl border-2 transition-all ${
                    selectedOption === option.id
                      ? "border-transparent bg-[#173991]"
                      : "border-[#282F44] bg-transparent hover:border-[#3A4159]"
                  }`}
                >
                  <div className="flex items-center justify-center gap-3">
                    {option.icon && (
                      <option.icon className="w-6 h-6 text-white" />
                    )}
                    <span className="text-white text-xl font-medium">
                      {option.label}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            <div className="mb-8">
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && selectedOption && !isUpdating) {
                    e.preventDefault();
                    e.stopPropagation();
                    onFinish();
                  }
                }}
                placeholder="Provide a brief description of the outcome"
                className="w-full px-4 py-4 bg-transparent border-2 border-[#282F44] rounded-xl text-white placeholder-[#8086A0] focus:outline-none focus:border-[#3A4159] transition-colors"
              />
            </div>
          </div>
        </div>

        <WarehouseButtonsFooter>
          <WarehouseButtonGroup>
            <WarehouseButton
              variant="default"
              onClick={onBack}
              disabled={isUpdating}
            >
              Back
            </WarehouseButton>
            <WarehouseButton
              variant="primary"
              onClick={onFinish}
              disabled={isUpdating || !selectedOption}
            >
              {isUpdating ? <CircularProgress size={24} /> : "Finish"}
            </WarehouseButton>
          </WarehouseButtonGroup>
        </WarehouseButtonsFooter>
      </div>
    </FullscreenModal>
  );
};

ProcessingConfirmation.title = TITLE;
